import React from 'react';

import { CollapsibleValue } from '@/components/molecules/CollapsibleValue';
import { CopyableValue } from '@/components/molecules/CopyableValue';
import { useTranslator } from '@/hooks/useTranslator';
import { PaymentRailType } from '@/types/currency';
import { DetailsField } from '@/types/detailsField';
import { FiatAccountType } from '@/types/fiatAccountType';
import { Transaction } from '@/types/transaction';
import { isSwiftLocalInfo } from '@/types/transaction.ext';

interface SwiftInfo {
  readonly uetr: string | null;
  readonly imad: string | null;
  readonly omad: string | null;
  readonly pacs008: string | null;
}

export const usePaymentIdentifiersFields = (
  transaction: Transaction | undefined,
): ReadonlyArray<DetailsField | null> => {
  const localInfo = transaction?.localInfo;
  const paymentType = transaction?.paymentType;
  const blockchainInfo = transaction?.blockchainInfo;
  const isSwift = paymentType === FiatAccountType.swift || paymentType === PaymentRailType.Swift;
  const tr = useTranslator();

  const swiftInfo = React.useMemo((): SwiftInfo => {
    if (localInfo && isSwiftLocalInfo(localInfo)) {
      return {
        uetr: localInfo.uetr,
        imad: localInfo.imad ?? null,
        omad: localInfo.omad ?? null,
        pacs008: localInfo.pacs008 ?? null,
      };
    }
    return { uetr: null, imad: null, omad: null, pacs008: null };
  }, [localInfo]);

  const withdrawalHash = blockchainInfo?.withdrawalTransactionHash ?? null;
  const depositHash = blockchainInfo?.depositTransactionHash ?? null;

  return React.useMemo(
    (): ReadonlyArray<DetailsField | null> => [
      swiftInfo.uetr
        ? {
            key: 'uetr',
            label: tr('TRANSACTION_UETR'),
            value: <CopyableValue value={swiftInfo.uetr} />,
            columnSpan: 2,
          }
        : null,
      swiftInfo.imad && !isSwift
        ? {
            key: 'imad',
            label: tr('TRANSACTION_IMAD'),
            value: <CopyableValue value={swiftInfo.imad} />,
            columnSpan: 2,
          }
        : null,
      swiftInfo.omad && !isSwift
        ? {
            key: 'omad',
            label: tr('TRANSACTION_OMAD'),
            value: <CopyableValue value={swiftInfo.omad} />,
            columnSpan: 2,
          }
        : null,
      swiftInfo.pacs008
        ? {
            key: 'pacs008',
            label: tr('TRANSACTION_PACS008'),
            value: <CollapsibleValue value={swiftInfo.pacs008} previewLines={3} />,
            columnSpan: 2,
          }
        : null,
      depositHash
        ? {
            key: 'depositTransactionHash',
            label: tr('DEPOSIT_TRANSACTION_HASH'),
            value: <CopyableValue value={depositHash} />,
            columnSpan: 2,
          }
        : null,
      withdrawalHash
        ? {
            key: 'withdrawalTransactionHash',
            label: tr('WITHDRAWAL_TRANSACTION_HASH'),
            value: <CopyableValue value={withdrawalHash} />,
            columnSpan: 2,
          }
        : null,
    ],
    [swiftInfo, tr, isSwift, depositHash, withdrawalHash],
  );
};
